/**
 * FAQ content — faithful port of the reference mockup's FAQ_DATA
 * (docs/reference_design/vertailutyokalu.html). Structure: an array of
 * categories, each with id, FI/EN title + intro, and a list of {q, a} items
 * in both languages. Answers contain trusted, hand-authored HTML (no user
 * input) rendered as-is in the FAQ modal.
 */

export interface FaqItem {
  q_fi: string;
  q_en: string;
  a_fi: string;
  a_en: string;
}

export interface FaqCategory {
  id: string;
  title_fi: string;
  title_en: string;
  intro_fi: string;
  intro_en: string;
  items: FaqItem[];
}

export const FAQ_DATA: FaqCategory[] = [
  {
    id: 'general',
    title_fi: 'Yleistä',
    title_en: 'General',
    intro_fi: 'Mikä Vertailutyökalu on ja kenelle se on tarkoitettu.',
    intro_en: 'What is the Vertailutyökalu and who is it for.',
    items: [
      {
        q_fi: 'Mikä Vertailutyökalu on?',
        q_en: 'What is the Vertailutyökalu?',
        a_fi: `<p>Vertailutyökalu on Suomen Metsätilastot Oy:n verkkopalvelu, joka kerää yhteen paikkaan eri myyntiportaaleissa julkaistut metsätilojen myynti-ilmoitukset ja laskee niistä suuntaa-antavia hinta-arvioita toteutuneiden kauppojen perusteella.</p>
<p>Työkalu vastaa kysymyksiin kuten: <em>"Mikä on tämän tilan todennäköinen kauppahinta?"</em>, <em>"Onko myyjän pyyntihinta linjassa markkinan kanssa?"</em> ja <em>"Mistä löytyy vertailukelpoisia kauppoja samasta maakunnasta?"</em></p>`,
        a_en: `<p>The Vertailutyökalu is an online service by Suomen Metsätilastot Oy that aggregates forest estate sales listings from various portals and computes indicative price estimates based on historical sale prices.</p>
<p>The tool answers questions like: <em>"What's the likely sale price of this estate?"</em>, <em>"Is the seller's asking price in line with the market?"</em>, and <em>"Where can I find comparable sales in the same region?"</em></p>`,
      },
      {
        q_fi: 'Kenelle työkalu on tarkoitettu?',
        q_en: 'Who is the tool for?',
        a_fi: `<p>Kohderyhmät:</p>
<ul>
<li><strong>Yksityisostajat</strong> jotka harkitsevat metsätilan ostoa ja haluavat ymmärtää onko pyyntihinta järkevä</li>
<li><strong>Metsätilan omistajat</strong> jotka miettivät myyntiä ja haluavat tietää oman tilansa markkina-arvon haarukan</li>
<li><strong>Metsänhoitoyhdistykset ja välittäjät</strong> jotka tarvitsevat nopean vertailupohjan toimeksiantoa varten</li>
<li><strong>Institutionaaliset sijoittajat</strong> jotka analysoivat metsätilakauppojen markkinakehitystä</li>
</ul>`,
        a_en: `<p>Target audiences:</p>
<ul>
<li><strong>Individual buyers</strong> considering forest estate purchase, wanting to gauge whether the asking price is reasonable</li>
<li><strong>Forest estate owners</strong> contemplating a sale and wanting to know the market range</li>
<li><strong>Forest associations and brokers</strong> needing a quick comparable base for client work</li>
<li><strong>Institutional investors</strong> analysing forest estate transaction trends</li>
</ul>`,
      },
      {
        q_fi: 'Voinko luottaa työkalun arvioon päätöksenteossani?',
        q_en: "Can I rely on the tool's estimate for decision-making?",
        a_fi: `<p>Vertailutyökalu antaa <strong>suuntaa-antavia arvioita</strong>, ei sitovia hinnoitteluja. Arvio perustuu tilastolliseen vertailuun samalla alueella toteutuneista kaupoista.</p>
<div class="faq-note">Suosittelemme käyttämään työkalun arviota yhtenä päätöksentekoa tukevana tietona muiden lähteiden rinnalla: oma maastokäynti, ammattilaisen tila-arvio, metsäsuunnitelma, ja mahdolliset paikalliset erityispiirteet (kaavoitus, suojelualueet, rakennuspaikat). Yhdellä klikkauksella saatu hinta ei korvaa asiantuntijan paikan päällä tekemää arviota.</div>`,
        a_en: `<p>The Vertailutyökalu provides <strong>indicative estimates</strong>, not binding valuations. The estimate is based on statistical comparison with completed sales in the same region.</p>
<div class="faq-note">We recommend using the tool's estimate as one supporting data point alongside other sources: your own site visit, a professional valuation, the forest management plan, and any local specifics (zoning, conservation areas, building sites). A one-click estimate doesn't replace on-site expert appraisal.</div>`,
      },
      {
        q_fi: 'Miten saan käyttöoikeudet palveluun?',
        q_en: 'How do I get access to the service?',
        a_fi: `<p>Rekisteröityminen on ilmaista. Voit luoda käyttäjätunnuksen "Rekisteröidy"-painikkeesta sivun yläreunasta.</p>
<p>Eri käyttöoikeustasot avaavat lisäominaisuuksia (esim. markkinakatsaus, datavienti). Jos tarvitset laajempia oikeuksia työkäyttöön tai oman organisaatiosi puolesta, ota yhteyttä palveluntarjoajaan.</p>`,
        a_en: `<p>Registration is free. You can create an account from the "Register" button at the top of the page.</p>
<p>Different access tiers unlock additional features (e.g. market overview, data export). For broader rights for work or organisational use, contact the service provider.</p>`,
      },
    ],
  },
  {
    id: 'pricing',
    title_fi: 'Hinta-arvio',
    title_en: 'Price estimate',
    intro_fi: 'Miten arvio syntyy ja miten sitä kannattaa tulkita.',
    intro_en: 'How the estimate is produced and how to interpret it.',
    items: [
      {
        q_fi: 'Miten hinta-arvio syntyy?',
        q_en: 'How is the price estimate produced?',
        a_fi: `<p>Arvio perustuu samankaltaisten metsätilojen toteutuneisiin kauppoihin. Työkalu etsii kohdetilan maakunnasta vertailutiloja joiden puuston tasokin on lähellä kohdetta, ja laskee niiden kauppahinnoista painotetun arvion.</p>
<p>Painotuksessa otetaan huomioon mm. tilan puuston rakenne (kasvupaikat, kehitysluokat, tukkipuun osuus) ja kaupan tuoreus — tuoreemmat ja samankaltaisempi tilat painottuvat enemmän.</p>`,
        a_en: `<p>The estimate is based on completed sales of similar forest estates. The tool finds comparables in the target's region whose stand volume is close to the target, and computes a weighted estimate from their sale prices.</p>
<p>The weighting accounts for the estate's stand structure (site classes, development phases, log share) and the recency of the sale — more recent and more similar estates carry more weight.</p>`,
      },
      {
        q_fi: 'Miksi arvio näytetään haarukkana (alaraja – mediaani – yläraja)?',
        q_en: 'Why is the estimate shown as a range (low – median – high)?',
        a_fi: `<p>Yksittäinen luku antaisi väärän mielikuvan tarkkuudesta. Vertailutiloissa on aina hajontaa — sama puuston taso voi tuottaa eri kauppahintoja paikallisten erityispiirteiden, kysynnän ja myyntiajan mukaan.</p>
<p>Haarukka kuvaa tämän hajonnan ja antaa kolme käyttökelpoista tarjoustasoa:</p>
<ul>
<li><strong>Alaraja (konservatiivinen):</strong> tarjous joka voi jäädä alle markkinatason, mutta jolla kauppa olisi pohjimmiltaan kannattava</li>
<li><strong>Mediaani:</strong> markkinan tyypillinen toteutunut hintataso</li>
<li><strong>Yläraja (aggressiivinen):</strong> tarjous joka todennäköisesti voittaa tarjouskilpailun</li>
</ul>`,
        a_en: `<p>A single number would give a false sense of precision. There is always variation among comparables — the same stand level can produce different sale prices depending on local features, demand, and time on market.</p>
<p>The range captures this variation and gives three useful bid levels:</p>
<ul>
<li><strong>Low (conservative):</strong> may fall below market, but the deal would still be fundamentally sound</li>
<li><strong>Median:</strong> the market's typical realised price</li>
<li><strong>High (aggressive):</strong> likely to win a bidding contest</li>
</ul>`,
      },
      {
        q_fi: 'Mikä on pyyntikerroin ja mikä summakerroin?',
        q_en: 'What are the asking-ratio and sum-value methods?',
        a_fi: `<p>Työkalu laskee kaksi rinnakkaista arviota:</p>
<ul>
<li><strong>Pyyntikerroin (oletus):</strong> kuinka suuri osuus pyyntihinnasta on tyypillisesti toteutunut kaupassa. Lopullinen ennuste = kohteen pyynti × tämä kerroin.</li>
<li><strong>Summakerroin:</strong> kuinka suuri osuus laskennallisesta summa-arvosta on tyypillisesti toteutunut kaupassa. Lopullinen ennuste = kohteen summa-arvo × tämä kerroin.</li>
</ul>
<p><strong>Summa-arvo</strong> tarkoittaa metsänarvioinnin perusteella laskettua kiinteistön kokonaisarvoa. Pyyntihinta puolestaan on myyjän asettama hintapyyntö.</p>
<p>Jälkitestauksessa pyyntikerroin on osoittautunut tarkemmaksi useimmissa tilanteissa, koska pyyntihinta kantaa jo paljon paikallista markkinatietoa (rakennukset, sijainti, tieyhteys, kysyntä). Siksi pyyntikerroin on oletusmenetelmä.</p>`,
        a_en: `<p>The tool computes two parallel estimates:</p>
<ul>
<li><strong>Asking-ratio (default):</strong> what fraction of the asking price has typically been realised in actual sales. Final estimate = target's asking × this ratio.</li>
<li><strong>Sum-value ratio:</strong> what fraction of the calculated summa-value has typically been realised. Final estimate = target's summa-value × this ratio.</li>
</ul>
<p><strong>Summa-value</strong> is the property's total value calculated through forest valuation. The asking price is the seller's listed price.</p>
<p>Backtesting has shown the asking-ratio to be more accurate in most situations, because the asking price already encodes local market knowledge (buildings, location, road access, demand). That's why asking-ratio is the default.</p>`,
      },
      {
        q_fi: 'Mitä tarkoittaa "menetelmät eroavat" -ilmoitus?',
        q_en: 'What does the "methods diverge" notice mean?',
        a_fi: `<p>Jos pyynti- ja summakerroin tuottavat selvästi eri tulokset, työkalu näyttää huomautuksen. Tällöin yksi seuraavista tilanteista yleensä pätee:</p>
<ul>
<li>Myyjä on hinnoitellut tilan poikkeavasti — joko tarkoituksellisesti alle tai yli laskennallisen arvon</li>
<li>Vertailupohjassa on jotain epätyypillistä (esim. harvinainen tilatyyppi)</li>
<li>Summa-arvon laskennassa on epätarkkuutta</li>
</ul>
<p>Varoitus on signaali katsoa tilaa tarkemmin: onko siinä jotain, mikä joko nostaa tai laskee arvoa systemaattisesti?</p>`,
        a_en: `<p>If the asking-ratio and summa-ratio produce significantly different results, the tool shows a notice. One of the following typically applies:</p>
<ul>
<li>The seller has priced unconventionally — intentionally below or above the calculated value</li>
<li>Something unusual in the comparable pool (rare estate type)</li>
<li>The summa-value calculation has imprecision</li>
</ul>
<p>The warning is a signal to look closer: is there something systematic raising or lowering the value?</p>`,
      },
    ],
  },
  {
    id: 'comps',
    title_fi: 'Vertailutilat ja luotettavuus',
    title_en: 'Comparables and confidence',
    intro_fi: 'Miten vertailutiloja käytetään ja mitä luotettavuustasot tarkoittavat.',
    intro_en: 'How comparables are used and what the confidence levels mean.',
    items: [
      {
        q_fi: 'Mitä "korkea", "kohtuullinen" ja "matala" luotettavuus tarkoittavat?',
        q_en: 'What do "high", "moderate", and "low" confidence mean?',
        a_fi: `<p>Luotettavuus kuvaa kuinka hyvin vertailupohja tukee ennustetta:</p>
<ul>
<li><strong>Korkea:</strong> vertailutiloja löytyy paljon ja ne ovat profiiliprofiililtaan lähellä kohdetta. Ennusteeseen voi luottaa tavanomaisesti.</li>
<li><strong>Kohtuullinen:</strong> vertailutiloja on, mutta ne eivät vastaa kohdetta yhtä hyvin tai määrä on rajallisempi. Ennuste on suuntaa-antava mutta lukutuhamarginaalia on enemmän.</li>
<li><strong>Matala:</strong> vertailupohja on heikko (vähän tiloja tai ne eivät vastaa kohdetta hyvin). Suosittelemme tukeutumaan ennusteen rinnalla muihin tietolähteisiin, kuten paikan päällä tehtyyn asiantuntija-arvioon.</li>
</ul>`,
        a_en: `<p>Confidence reflects how well the comparable base supports the prediction:</p>
<ul>
<li><strong>High:</strong> many comparables found and they match the target's profile well. The estimate can be trusted within normal limits.</li>
<li><strong>Moderate:</strong> comparables exist, but they don't match the target as well or the pool is more limited. The estimate is indicative but with a wider margin.</li>
<li><strong>Low:</strong> the comparable base is weak (few estates or poor match). We recommend complementing the estimate with other sources such as an on-site expert valuation.</li>
</ul>`,
      },
      {
        q_fi: 'Miten vertailutilat valitaan?',
        q_en: 'How are comparables selected?',
        a_fi: `<p>Vertailutilat valitaan kahdessa vaiheessa:</p>
<ol>
<li><strong>Maantieteellinen ja puustollinen rajaus:</strong> mukaan otetaan vain saman maakunnan tilat, joiden puuston taso on lähellä kohdetta. Tämä varmistaa että vertailupohja on alueellisesti ja rakenteellisesti relevantti.</li>
<li><strong>Profiilipisteytys:</strong> jokaiselle vertailutilalle lasketaan samankaltaisuuspistemäärä kohteen kanssa kasvupaikkojen, kehitysluokkien ja puuston rakenteen perusteella. Eniten samankaltaiset tilat painottuvat lopullisessa arviossa.</li>
</ol>
<p>Lisäksi tuoreemmat kaupat painottuvat enemmän kuin vanhat, jotta markkinakehitys huomioidaan.</p>`,
        a_en: `<p>Comparables are selected in two stages:</p>
<ol>
<li><strong>Geographic and forestry filter:</strong> only same-region estates whose stand level is close to the target are included. This ensures the comparable base is regionally and structurally relevant.</li>
<li><strong>Profile scoring:</strong> each comparable receives a similarity score based on site classes, development phases, and stand composition. The most similar estates weigh most in the final estimate.</li>
</ol>
<p>More recent sales also weigh more than older ones, so market trends are reflected.</p>`,
      },
      {
        q_fi: 'Miksi joillakin tiloilla ei näy arviota lainkaan?',
        q_en: 'Why do some estates show no estimate at all?',
        a_fi: `<p>Arvio jää laskematta jos:</p>
<ul>
<li>Vertailutiloja löytyy liian vähän — esimerkiksi harvinaisen tyyppisillä tiloilla</li>
<li>Tilalla ei ole tiedossa metsämaata tai puuston tilavuutta (puuttuvat lähtötiedot)</li>
<li>Tilalla ei ole pyyntihintaa (esim. "Tarjouskauppa, hinta sopimuksen mukaan")</li>
</ul>
<p>Näissä tapauksissa työkalu suosittelee asiantuntijan paikan päällä tekemää arviota.</p>`,
        a_en: `<p>An estimate is not produced if:</p>
<ul>
<li>Too few comparables — for example with rare estate types</li>
<li>The estate has no known forest land area or stand volume (missing source data)</li>
<li>The estate has no asking price (e.g. "Bid-based sale, price by agreement")</li>
</ul>
<p>In these cases the tool recommends an on-site expert appraisal.</p>`,
      },
    ],
  },
  {
    id: 'estates',
    title_fi: 'Omat tilat',
    title_en: 'My estates',
    intro_fi: 'Tilojen tallentaminen, jakaminen ja Omat tilat -näkymä.',
    intro_en: 'Saving and sharing estates, and the My Estates view.',
    items: [
      {
        q_fi: 'Mikä on Omat tilat -näkymä?',
        q_en: 'What is the My Estates view?',
        a_fi: `<p>Omat tilat on henkilökohtainen näkymäsi tiloista, joita olet tallentanut tai jotka joku on jakanut sinulle. Voit palata näihin tiloihin myöhemmin yhdellä klikkauksella.</p>
<p>Näkymästä näet kunkin tilan perustiedot ja pääset suoraan tilan kohdesivulle, jossa näkyy tilan analyysi.</p>`,
        a_en: `<p>My Estates is your personal view of estates you've saved or that someone has shared with you. You can return to these estates with one click later.</p>
<p>The view shows each estate's basic info and lets you go directly to the estate detail page with its analysis.</p>`,
      },
      {
        q_fi: 'Mitä eroa on "tallenna" ja "jaa" välillä?',
        q_en: 'What is the difference between "save" and "share"?',
        a_fi: `<p>Käyttöoikeustasostasi riippuen tilan kohdesivulla näkyy joko "Tallenna arvio" tai "Jaa arvio" -painike:</p>
<ul>
<li><strong>Tallenna arvio</strong> — lisää tilan omaan Omat tilat -näkymääsi. Tila näkyy vain sinulle.</li>
<li><strong>Jaa arvio</strong> — voit lähettää arvion toisen käyttäjän sähköpostiin. Vastaanottaja näkee tilan omassa Omat tilat -näkymässään, kun hän kirjautuu palveluun. Tämä toiminto on käytettävissä laajemmilla käyttöoikeuksilla.</li>
</ul>`,
        a_en: `<p>Depending on your access tier, the estate detail page shows either a "Save estimate" or "Share estimate" button:</p>
<ul>
<li><strong>Save estimate</strong> — adds the estate to your own My Estates view. Only you see it.</li>
<li><strong>Share estimate</strong> — lets you send the valuation to another user's email. The recipient sees the estate in their own My Estates when they log in. This feature is available on broader access tiers.</li>
</ul>`,
      },
      {
        q_fi: 'Voinko poistaa tilan Omat tilat -näkymästäni?',
        q_en: 'Can I remove an estate from My Estates?',
        a_fi: `<p>Kyllä. Omat tilat -taulukon jokaisen rivin oikealla puolella on "×"-poistonappi. Klikkaamalla sitä avautuu vahvistusdialogi, jonka jälkeen rivi poistuu omasta näkymästäsi.</p>
<p>Poistaminen vaikuttaa <strong>vain sinun näkymääsi</strong>. Alkuperäistä tilatietoa tai jakajan kopiota ei poisteta.</p>`,
        a_en: `<p>Yes. Every row in My Estates has a "×" remove button on the right. Clicking it opens a confirmation, after which the row is removed from your view.</p>
<p>Removal only affects <strong>your view</strong>. The underlying estate data or the sharer's copy is not removed.</p>`,
      },
      {
        q_fi: "Voinko jakaa tilan käyttäjälle, joka ei vielä ole rekisteröitynyt?",
        q_en: "Can I share to someone who isn't registered yet?",
        a_fi: `<p>Kyllä. Voit jakaa minkä tahansa sähköpostiosoitteen, myös sellaisen jolla ei vielä ole tunnusta.</p>
<p>Kun kyseinen henkilö rekisteröityy myöhemmin <strong>tarkalleen samalla sähköpostiosoitteella</strong>, jako aktivoituu ja näkyy heidän Omat tilat -näkymässään.</p>
<div class="faq-note">Tärkeää: sähköpostiosoitteen on oltava täsmälleen sama (kirjainkoolla ei ole väliä). Pienikin ero tarkoittaa että jako ei näy.</div>
<div class="faq-note">HUOM: vastaanottajaa ei tiedoteta automaattisesti. Sinun pitää itse kertoa hänelle, että olet jakanut tilan.</div>`,
        a_en: `<p>Yes. You can share to any email address, including one without an account.</p>
<p>When that person later registers <strong>with the exact same email</strong>, the share activates and appears in their My Estates view.</p>
<div class="faq-note">Important: the email must match exactly (case-insensitive). Any difference means the share won't appear.</div>
<div class="faq-note">NOTE: the recipient is not notified automatically. You must tell them yourself that you've shared an estate.</div>`,
      },
    ],
  },
  {
    id: 'data',
    title_fi: 'Tietolähteet ja kattavuus',
    title_en: 'Data sources and coverage',
    intro_fi: 'Mistä data tulee ja kuinka usein se päivittyy.',
    intro_en: 'Where the data comes from and how often it updates.',
    items: [
      {
        q_fi: 'Mistä työkalun data tulee?',
        q_en: "Where does the tool's data come from?",
        a_fi: `<p>Aineisto koostuu kahdesta osasta:</p>
<ul>
<li><strong>Myynnissä olevat tilat:</strong> Kerätään säännöllisesti yleisimmistä metsätilojen myyntiportaaleista. Ilmoituksissa olevat tiedot säilytetään: pyyntihinta, pinta-ala, puuston taso, kasvupaikkajakauma, kehitysluokat, ranta- ja tietyypit jne.</li>
<li><strong>Toteutuneet kaupat:</strong> Sisältää tilat, jotka ovat aiemmin olleet myynnissä ja joiden lopullinen kauppahinta on rekisteröity. Niistä muodostuu vertailupohja.</li>
</ul>
<p>Tietokanta sisältää tuhansia tiloja eri puolilta Suomea.</p>`,
        a_en: `<p>The dataset has two parts:</p>
<ul>
<li><strong>For-sale estates:</strong> Collected regularly from common forest estate sales portals. All listing fields are preserved: asking price, area, stand level, site distribution, development classes, shoreline and road access, etc.</li>
<li><strong>Completed sales:</strong> Estates previously listed for which the final sale price has been recorded. They form the comparable base.</li>
</ul>
<p>The database contains thousands of estates from across Finland.</p>`,
      },
      {
        q_fi: 'Kuinka usein data päivittyy?',
        q_en: 'How often is data updated?',
        a_fi: `<p>Tietokanta päivittyy säännöllisesti. Päivityspäivämäärä näkyy sivun alalaidan footerissa muodossa "Tiedot päivitetty: pp.kk.vvvv".</p>
<p>Käytännössä:</p>
<ul>
<li>Uudet myynti-ilmoitukset lisätään aineistoon viiveellä — yleensä muutamien päivien tai viikkojen kuluessa siitä, kun ilmoitus on julkaistu portaalissa</li>
<li>Toteutuneet kauppahinnat saadaan portaalien sulkemisilmoituksista tai kauppakirjoista, myös tyypillisesti viiveellä</li>
<li>Vanhentuneet (myydyt tai vedetyt) ilmoitukset poistuvat tai siirtyvät "toteutunut kauppa" -kategoriaan</li>
</ul>`,
        a_en: `<p>The database updates regularly. The update date appears in the footer as "Data updated: dd/mm/yyyy".</p>
<p>In practice:</p>
<ul>
<li>New listings are added with some delay — typically a few days or weeks after publication on the portal</li>
<li>Completed sale prices come from portal closing notices or deed records, also with delay</li>
<li>Stale (sold or withdrawn) listings are removed or move to the "completed sale" category</li>
</ul>`,
      },
      {
        q_fi: 'Mitkä alueet ovat mukana?',
        q_en: 'Which regions are covered?',
        a_fi: `<p>Kaikki Suomen maakunnat ovat mukana. Tietokannan kattavuus painottuu sinne, missä metsätilakauppaa on eniten — käytännössä Pohjois- ja Itä-Suomen suurissa maakunnissa on eniten vertailupohjaa.</p>
<p>Maakunnissa joissa kauppaa on vähemmän, työkalu antaa silti arvion, mutta luotettavuus voi pudota "kohtuulliseksi" tai "matalaksi" jos vertailutiloja ei löydy riittävästi.</p>`,
        a_en: `<p>All Finnish regions are covered. Coverage emphasises where forest estate trade is most active — in practice the large regions of Northern and Eastern Finland have the strongest comparable base.</p>
<p>In regions with less trade the tool still produces an estimate, but confidence may drop to "moderate" or "low" if enough comparables can't be found.</p>`,
      },
      {
        q_fi: 'Miksi tilan kartta näyttää ympäröivän alueen mutta ei tilan tarkkoja rajoja?',
        q_en: 'Why does the map show the surrounding area but not exact estate boundaries?',
        a_fi: `<p>Kartta käyttää Maanmittauslaitoksen avointa karttarajapintaa ja näyttää oletuksena alueen taustakartan. Tilan kiinteistörajat näkyvät, jos käyttäjä aktivoi "Kiinteistörajat"-tason karttakontrollissa.</p>
<p>Lisäksi: jos kohde on ns. <strong>määräala</strong> (osa kantakiinteistöstä), näytetään koko kantakiinteistön rajat — todelliset määräalan rajat eivät vielä ole rekisterissä. Tämä on yleinen tilanne metsätilakaupoissa.</p>`,
        a_en: `<p>The map uses the National Land Survey's open mapping API and shows the area's background map by default. Estate parcel boundaries appear only if you enable the "Parcel boundaries" layer in map controls.</p>
<p>Note: if the target is a <strong>partial parcel</strong> (a portion of a base property), the boundaries shown are those of the full base property — the actual partial-parcel boundaries are not yet in the registry. This is common in forest estate sales.</p>`,
      },
    ],
  },
  {
    id: 'features',
    title_fi: 'Toiminnot ja näkymät',
    title_en: 'Features and views',
    intro_fi: 'Markkinayhteenveto, tallennetut haut, kieli, teema ja tulostaminen.',
    intro_en: 'Market overview, saved searches, language, theme and printing.',
    items: [
      {
        q_fi: 'Mikä on Markkinayhteenveto?',
        q_en: 'What is the Market Overview?',
        a_fi: `<p>Markkinayhteenveto on katsaus metsätilakauppojen markkinakehitykseen. Se näyttää:</p>
<ul>
<li>Vuositason keskiarvot toteutuneista kaupoista — kpl, pinta-ala, kokonaiseuro, keskim. €/ha</li>
<li>Kvartaaliyhteenvedon viime ajan kehityksestä</li>
<li>Vuosipaneelit, joissa mediaaniarvot ja suosituimmat maakunnat</li>
<li>Maakuntataulukon ja ostajatyyppien jakauman</li>
</ul>
<p>Näkymässä voi vaihtaa "Myyntiin tulleet" (kaikki ilmoitukset) ja "Toteutuneet kaupat" (vain päättyneet) -tilojen välillä.</p>
<p>Markkinayhteenveto on käytettävissä laajemmilla käyttöoikeustasoilla.</p>`,
        a_en: `<p>The Market Overview is a snapshot of forest estate transaction trends. It shows:</p>
<ul>
<li>Yearly averages of completed sales — count, area, total euros, avg €/ha</li>
<li>Quarterly view of recent activity</li>
<li>Per-year panels with median values and top regions</li>
<li>Region table and buyer type distribution</li>
</ul>
<p>You can switch between "Came on market" (all listings) and "Completed sales" modes.</p>
<p>The Market Overview is available on broader access tiers.</p>`,
      },
      {
        q_fi: 'Miten tallennetut haut toimivat?',
        q_en: 'How do saved searches work?',
        a_fi: `<p>Tallennetut haut -toiminnolla voit tallentaa nykyisen suodatuksen (maakunta, pinta-alaraja, hintahaarukka, lajittelu) nimellä ja palata siihen myöhemmin yhdellä klikkauksella.</p>
<p>Esimerkkejä:</p>
<ul>
<li>"Pohjois-Karjala, yli 30 ha, alle 200 k€" — säännöllinen seuranta tietyssä kohdekategoriassa</li>
<li>"Kainuu, korkea puuston taso" — laadukkaammat kohteet tietyssä maakunnassa</li>
</ul>
<p>Tallennetut haut ovat henkilökohtaisia ja säilyvät selaimessasi. Voit poistaa niitä "Hallitse tallennettuja hakuja..." -valinnasta.</p>`,
        a_en: `<p>Saved searches let you save the current filter (region, area threshold, price range, sort) under a name and return to it later with one click.</p>
<p>Examples:</p>
<ul>
<li>"Pohjois-Karjala, over 30 ha, under 200k€" — recurring monitoring of a category</li>
<li>"Kainuu, high stand level" — higher-quality estates in a region</li>
</ul>
<p>Saved searches are personal and stored in your browser. You can delete them via "Manage saved searches…".</p>`,
      },
      {
        q_fi: 'Miten voin vaihtaa kieltä ja teemaa?',
        q_en: 'How do I change language and theme?',
        a_fi: `<p>Sivun yläosassa oikealla on yhdistetty laatikko, jossa on kaksi valintaa:</p>
<ul>
<li><strong>Kieli:</strong> FIN / ENG -napit</li>
<li><strong>Teema:</strong> Kaksi värivalintaa — vaalea ja tumma</li>
</ul>
<p>Asetukset tallentuvat selaimeen, joten ne säilyvät seuraavissa istunnoissa. Tumma teema on oletuksena.</p>
<p>Kielenvaihto vaikuttaa kaikkiin teksteihin: kirjautumislomakkeisiin, navigaatioon, kohdesivun analyyseihin ja virheviesteihin.</p>`,
        a_en: `<p>Top right of the page has a combined box with two selectors:</p>
<ul>
<li><strong>Language:</strong> FIN / ENG buttons</li>
<li><strong>Theme:</strong> Two colour swatches — light and dark</li>
</ul>
<p>Settings persist in the browser between sessions. Dark theme is the default.</p>
<p>Language switch affects all text: login forms, navigation, estate detail analysis, and error messages.</p>`,
      },
      {
        q_fi: 'Voinko tulostaa kohdesivun?',
        q_en: 'Can I print the estate detail page?',
        a_fi: `<p>Kyllä. Käytä selaimen tulostustoimintoa (yleensä Ctrl+P tai ⌘+P). Työkalussa on erillinen tulostustyyli, joka:</p>
<ul>
<li>Piilottaa navigaation, footer-palkin, modaalit ja painikkeet</li>
<li>Säilyttää otsikon, kohteen tiedot, arvion, kartan ja vertailutaulukon</li>
<li>Käyttää A4-marginaaleja ja kompaktia mustavalkoista layoutia</li>
</ul>`,
        a_en: `<p>Yes. Use the browser's print function (usually Ctrl+P or ⌘+P). The tool has a dedicated print stylesheet that:</p>
<ul>
<li>Hides navigation, footer, modals and buttons</li>
<li>Preserves the title, estate details, estimate, map and comparable table</li>
<li>Uses A4 margins and a compact black-and-white layout</li>
</ul>`,
      },
    ],
  },
  {
    id: 'security',
    title_fi: 'Tietoturva ja yksityisyys',
    title_en: 'Security and privacy',
    intro_fi: 'Henkilötietojen käsittely, salasanat ja monilaite-käyttö.',
    intro_en: 'Personal data handling, passwords and multi-device use.',
    items: [
      {
        q_fi: 'Miten henkilötietojani käsitellään?',
        q_en: 'How is my personal data handled?',
        a_fi: `<p>Rekisteröityessäsi annat:</p>
<ul>
<li>Etu- ja sukunimi</li>
<li>Osoite</li>
<li>Sähköpostiosoite (toimii myös käyttäjätunnuksena)</li>
<li>Salasana (tallennetaan vain hashina, ei selkokielisenä)</li>
</ul>
<p>Tietojasi käsitellään GDPR:n mukaisesti vain palvelun toimintaa varten. Niitä ei luovuteta kolmansille osapuolille ilman erillistä suostumustasi. Hyväksyt tämän rekisteröityessäsi.</p>
<p>Voit pyytää tietojesi poistamista olemalla yhteydessä palveluntarjoajaan.</p>`,
        a_en: `<p>At registration you provide:</p>
<ul>
<li>First and last name</li>
<li>Address</li>
<li>Email address (also serves as username)</li>
<li>Password (stored only as a hash, never plaintext)</li>
</ul>
<p>Your data is processed in accordance with GDPR only for service operation. It is not shared with third parties without your separate consent. You agree to this at registration.</p>
<p>You can request data deletion by contacting the service provider.</p>`,
      },
      {
        q_fi: 'Miten salasanasuojaus toimii?',
        q_en: 'How is password security handled?',
        a_fi: `<p>Salasanat säilytetään hash-muodossa. Selkokielinen salasana ei koskaan tallennu järjestelmään.</p>
<p>Käytännössä tämä tarkoittaa:</p>
<ul>
<li>Palveluntarjoaja ei voi nähdä salasanaasi</li>
<li>Jos unohdat salasanan, sitä ei voi palauttaa — sen voi vain nollata ja saada uuden</li>
<li>Suosittelemme käyttämään uniikkia, vähintään 8 merkin salasanaa</li>
</ul>`,
        a_en: `<p>Passwords are stored as a hash. Plaintext passwords are never stored.</p>
<p>In practice this means:</p>
<ul>
<li>The service provider cannot see your password</li>
<li>If you forget it, it cannot be recovered — only reset</li>
<li>We recommend a unique, at least 8-character password</li>
</ul>`,
      },
      {
        q_fi: 'Voinko käyttää palvelua mobiilissa?',
        q_en: 'Can I use the service on mobile?',
        a_fi: `<p>Kyllä. Käyttöliittymä on responsiivinen ja toimii sekä puhelimella, tabletilla että työpöydällä. Kohdesivu skaalautuu kapeampiin näyttöihin, kartta toimii kosketuksella ja taulukoissa on vaakavieritys.</p>
<p>Kaikki ydintoiminnot (selaaminen, suodatus, kohdetietojen tarkastelu, jakaminen) toimivat mobiilissa.</p>`,
        a_en: `<p>Yes. The interface is responsive and works on phone, tablet and desktop. The estate detail page scales to narrower screens, the map supports touch, and tables have horizontal scroll.</p>
<p>All core features (browsing, filtering, viewing estate details, sharing) work on mobile.</p>`,
      },
    ],
  },
  {
    id: 'taxation',
    title_fi: 'Metsävähennys ja verotus',
    title_en: 'Forest deduction and taxation',
    intro_fi: 'Vertailutyökalun käyttömaksu metsätilan tulonhankinnan kuluna.',
    intro_en: 'Vertailutyökalu subscription as an expense of forest income generation.',
    items: [
      {
        q_fi: 'Onko Vertailutyökalun käyttömaksu metsävähennyskelpoinen?',
        q_en: 'Is the Vertailutyökalu subscription deductible as a forest expense?',
        a_fi: `<p>Pääsääntöisesti kyllä. Jotta meno on metsävähennyskelpoinen, sen tulee liittyä olemassa olevan metsätilan hoitoon ja tulonhankintaan.</p>
<p>Vertailutyökalu on metsätilastollinen analyysipalvelu, joka tukee suoraan metsänomistuksen taloudellista hallintaa:</p>
<ul>
<li>Markkina-arvoanalyysit jo omistettujen tilojen arvon seurantaan</li>
<li>Vertailutiedot toteutuneista kaupoista samalta alueelta</li>
<li>Lisäkauppojen suunnittelu (esim. naapuritilan ostotarjouksen arviointi tulonhankintatarkoituksessa)</li>
<li>Myyntipäätösten tuki ajantasaisella markkinatiedolla</li>
</ul>
<p>Nämä käyttötarkoitukset tukevat metsätalouden harjoittamista ja tulonhankintaa, jolloin käyttömaksu on tyypillisesti vähennettävissä metsätalouden tulosta.</p>
<div class="faq-note"><strong>Tärkeää:</strong> Vähennyskelpoisuus riippuu aina käyttötarkoituksesta ja oman tilanteesi yksityiskohdista. Vahvista vähennyskelpoisuus omasta verotoimistostasi tai kirjanpitäjältäsi ennen ilmoitusta. Yksityisluontoinen, tulonhankintaan liittymätön käyttö ei ole vähennyskelpoinen.</div>`,
        a_en: `<p>As a general rule, yes. For an expense to be deductible as a forest cost, it must relate to the management of an existing forest property and to income generation.</p>
<p>The Vertailutyökalu is a forest analytics service that directly supports the financial management of forest ownership:</p>
<ul>
<li>Market-value analysis for monitoring the value of estates you already own</li>
<li>Comparable data on completed sales in the same area</li>
<li>Planning of additional purchases (e.g. evaluating a bid on a neighbouring estate for income-generating purposes)</li>
<li>Supporting sale decisions with up-to-date market information</li>
</ul>
<p>These uses support the operation of forestry and income generation, so the subscription cost is typically deductible from forestry income.</p>
<div class="faq-note"><strong>Important:</strong> Deductibility always depends on the actual use and details of your personal situation. Confirm deductibility with your local tax office or accountant before filing. Purely private use unrelated to income generation is not deductible.</div>`,
      },
      {
        q_fi: 'Mitä tositetta tarvitsen kirjanpitoa varten?',
        q_en: 'What receipt do I need for bookkeeping?',
        a_fi: `<p>Saat palvelumaksusta sähköisen laskun tai kuitin, joka sisältää kaikki kirjanpidossa vaadittavat tiedot:</p>
<ul>
<li>Palveluntarjoajan nimi ja Y-tunnus (Suomen Metsätilastot Oy)</li>
<li>Laskun päivämäärä ja numero</li>
<li>Palvelun kuvaus ja ajanjakso</li>
<li>Hinta ja ALV erikseen eriteltynä</li>
</ul>
<p>Säilytä tosite kirjanpitolain edellyttämän ajan (yleensä 6 vuotta verovuoden päättymisen jälkeen). Lasku ilmoitetaan veroilmoituksessa metsätalouden vuosimenona.</p>`,
        a_en: `<p>You receive an electronic invoice or receipt for the service charge containing all bookkeeping-required information:</p>
<ul>
<li>Service provider name and business ID (Suomen Metsätilastot Oy)</li>
<li>Invoice date and number</li>
<li>Service description and period</li>
<li>Price and VAT itemised separately</li>
</ul>
<p>Retain the receipt for the period required by accounting law (typically 6 years after the end of the tax year). The invoice is reported in the tax return as an annual forestry expense.</p>`,
      },
      {
        q_fi: 'Tarvitsenko verkkopalvelun lisäksi muuta dokumentaatiota?',
        q_en: 'Do I need additional documentation beyond the online service?',
        a_fi: `<p>Itse Vertailutyökalu on tilauspohjainen verkkopalvelu — saat laskun tilausjaksostasi, joka käy tositteeksi kirjanpidossa. Erillistä tulostetta tai raporttia ei tarvita kustannuksen vähentämiseen.</p>
<p>Jos kuitenkin haluat dokumentoida palvelun käyttöä tarkemmin, voit:</p>
<ul>
<li>Tulostaa kohdesivuja PDF-muotoon (selaimen tulostustoiminto, Ctrl+P / ⌘+P) ja arkistoida nämä analyysit metsäkirjanpitoosi</li>
<li>Tason 3 käyttöoikeudella myös viedä vertailuaineistoa CSV-muodossa omiin laskelmiisi</li>
</ul>
<p>Nämä eivät ole vähennyksen edellytys, mutta tukevat tilanteita joissa haluat osoittaa palvelun käytön liittyvän nimenomaan tulonhankintaan (esim. mahdollisen verotarkastuksen yhteydessä).</p>`,
        a_en: `<p>The Vertailutyökalu itself is a subscription-based online service — you receive an invoice for your subscription period that serves as the bookkeeping receipt. No separate printout or report is required for the deduction.</p>
<p>If you want to document your use of the service more thoroughly, you can:</p>
<ul>
<li>Print estate detail pages to PDF (browser print function, Ctrl+P / ⌘+P) and archive these analyses in your forestry bookkeeping</li>
<li>With Level 3 access, also export comparable data in CSV format for your own calculations</li>
</ul>
<p>These are not requirements for deduction, but support situations where you want to demonstrate that service use specifically relates to income generation (e.g. in case of a tax audit).</p>`,
      },
    ],
  },
  {
    id: 'interpretation',
    title_fi: 'Tulkinta ja vastuut',
    title_en: 'Interpretation and responsibilities',
    intro_fi: 'Mitä arvio EI ole, milloin asiantuntijaa tarvitaan ja miten työkalun antamaa tietoa kannattaa tulkita.',
    intro_en: "What the estimate is NOT, when to seek an expert, and how to interpret the tool's output.",
    items: [
      {
        q_fi: 'Mitä arvio EI ole?',
        q_en: 'What the estimate is NOT',
        a_fi: `<p>Vertailutyökalun arvio <strong>ei ole</strong>:</p>
<ul>
<li><strong>Sitova hinnoittelu</strong> — ei vaikuta veronkantoon, perinnönjakoon eikä juridiseen omistuksenvahvistukseen</li>
<li><strong>Virallinen kiinteistöarvio</strong> — siihen tarvitaan auktorisoidun kiinteistönarvioitsijan (AKA) lausunto</li>
<li><strong>Vakuusarvo</strong> — pankki tai vakuutusyhtiö ei hyväksy työkalun arviota lainavakuudeksi</li>
<li><strong>Tarjouksen vahvistus</strong> — toteutuneessa kaupassa hinta määräytyy myyjän ja ostajan välisessä sopimuksessa</li>
</ul>
<p>Vertailutyökalu on neuvotteluapu ja analyysityökalu — ei korvaa juridista tai ammatillista neuvontaa.</p>`,
        a_en: `<p>The Vertailutyökalu estimate is NOT:</p>
<ul>
<li><strong>A binding valuation</strong> — doesn't affect tax assessment, inheritance distribution or legal ownership confirmation</li>
<li><strong>An official property appraisal</strong> — that requires a licensed property valuer (AKA in Finland)</li>
<li><strong>A collateral value</strong> — banks or insurers won't accept the tool's estimate as loan collateral</li>
<li><strong>A bid confirmation</strong> — actual sale price is determined by the seller-buyer agreement</li>
</ul>
<p>The Vertailutyökalu is a negotiation aid and analysis tool — it does not replace legal or professional advice.</p>`,
      },
      {
        q_fi: 'Milloin kannattaa tilata asiantuntijan arvio?',
        q_en: 'When should I order an expert appraisal?',
        a_fi: `<p>Suosittelemme asiantuntijan tila-arviota seuraavissa tilanteissa:</p>
<ul>
<li><strong>Iso kauppasumma</strong> — kun kauppa on yli 200 000 €, ammattilaisen arvio on kustannustehokas verrattuna mahdolliseen hinnoitteluvirheeseen</li>
<li><strong>Erityispiirteet</strong> — jos tilalla on rakennuksia, rantaviivaa, kaavavarauksia tai poikkeavia luontokohteita, joita työkalu ei voi arvioida</li>
<li><strong>Matala luotettavuus</strong> — kun työkalu näyttää "matala luotettavuus" -merkinnän</li>
<li><strong>Menetelmät eroavat merkittävästi</strong> — kun pyyntikerroin ja summakerroin tuottavat hyvin eri tuloksia</li>
<li><strong>Veroon liittyvät kaupat</strong> — perinnönjako, lahjat, jakokirjat — vaativat virallisen arvion</li>
<li><strong>Pankkilaina</strong> — vakuusarvioon tarvitaan AKA-pätevän arvioitsijan lausunto</li>
</ul>`,
        a_en: `<p>We recommend a professional appraisal in these situations:</p>
<ul>
<li><strong>Large transaction</strong> — when sale value exceeds €200,000, professional appraisal is cost-effective vs the cost of mispricing</li>
<li><strong>Special features</strong> — buildings, shoreline, zoning, or unusual natural features the tool can't assess</li>
<li><strong>Low confidence</strong> — when the tool shows the "low confidence" marker</li>
<li><strong>Methods diverge significantly</strong> — when asking and summa methods produce very different results</li>
<li><strong>Tax-related transfers</strong> — inheritance, gifts, partition deeds — require an official valuation</li>
<li><strong>Bank loans</strong> — collateral valuation requires a licensed AKA appraiser's statement</li>
</ul>`,
      },
      {
        q_fi: 'Voiko tilan arvoa nostaa erityispiirteillä, joita työkalu ei huomioi?',
        q_en: "Can features the tool doesn't account for raise the estate's value?",
        a_fi: `<p>Kyllä, ja näitä voi olla useita:</p>
<ul>
<li><strong>Rakennukset:</strong> Hyväkuntoinen kesämökki, asuinrakennus tai talousrakennus voi nostaa hintaa selvästi. Työkalu ei arvioi rakennusten arvoa erikseen.</li>
<li><strong>Ranta:</strong> Oma rantaviiva, etenkin järvi-, joki- tai merenrannalla, voi nostaa virkistys- ja jälleenmyyntiarvoa selvästi.</li>
<li><strong>Tieyhteys:</strong> Hyvä yksityistie nostaa tilan arvoa korjuu- ja virkistyskäytössä.</li>
<li><strong>Naapurin omistus:</strong> Jos olet jo viereisen tilan omistaja, kohdetilalla voi olla synergia-arvoa joka oikeuttaa korkeamman tarjouksen.</li>
<li><strong>Kaavoitus:</strong> Jos osa tilasta on kaavoitettu rakennuspaikoiksi, niiden arvo voi olla moninkertainen metsämaan arvoon nähden.</li>
<li><strong>Maisema- tai kulttuurikohteet:</strong> Erityisen kauniit maisemat, suojellut rakennukset tai historialliset kohteet voivat nostaa hintaa.</li>
</ul>
<p>Työkalu kuvaa <strong>metsämaan arvon</strong> samankaltaisten kauppojen perusteella. Edellä mainitut ovat lisäkomponentteja, jotka tulee arvioida erikseen.</p>`,
        a_en: `<p>Yes, several can:</p>
<ul>
<li><strong>Buildings:</strong> A well-maintained cottage, residence or outbuilding can significantly add to the price. The tool doesn't value buildings separately.</li>
<li><strong>Shoreline:</strong> Own shoreline, especially on a lake, river or sea, can significantly raise recreational and resale value.</li>
<li><strong>Road access:</strong> Good private road access raises both harvesting and recreational value.</li>
<li><strong>Adjacency:</strong> If you already own the neighbouring estate, the target may have synergy value justifying a higher bid.</li>
<li><strong>Zoning:</strong> If part of the estate is zoned for buildings, that portion can be worth many times the forest land value.</li>
<li><strong>Landscape or cultural sites:</strong> Particularly scenic, protected buildings or historical sites can raise the price.</li>
</ul>
<p>The tool reflects the <strong>forest land value</strong> based on similar sales. The above are additional components requiring separate assessment.</p>`,
      },
      {
        q_fi: 'Mikä on hyvä strategia tarjouskilpailussa?',
        q_en: "What's a good strategy in a bidding contest?",
        a_fi: `<p>Vertailutyökalu auttaa hahmottamaan markkinatasoa, mutta tarjouskilpailussa kannattaa lisäksi miettiä:</p>
<ul>
<li><strong>Kuinka tärkeä kohde on sinulle?</strong> — Jos kohde sopii poikkeuksellisen hyvin omiin tarpeisiisi, aggressiivinen tarjous voi olla perusteltu.</li>
<li><strong>Kuinka kova kilpailu on?</strong> — Vetovoimaisissa kohteissa (ranta, lähellä taajamaa) tarjouksia tulee enemmän. Konservatiivinen tarjous ei välttämättä riitä.</li>
<li><strong>Markkinakehitys:</strong> Markkinayhteenveto-näkymästä näkyy onko hinnat nousseet vai laskeneet viime kvartaaleissa.</li>
<li><strong>Pyyntihinta vs ennuste:</strong> Jos pyyntihinta on selvästi yli ennusteen, kohde on todennäköisesti ylihinnoiteltu. Jos selvästi alle, myyjä voi olla motivoitunut myymään nopeasti.</li>
<li><strong>Aikaikkuna:</strong> Onko kaupan tekemiselle deadline (esim. perinnönjako)? Voitko venyttää neuvotteluja vai tarvitsetko nopean ratkaisun?</li>
</ul>
<p>Itse työkalun tarjousehdotukset (alaraja / mediaani / yläraja) antavat lähtöpisteet, mutta lopullinen tarjous on aina sinun strateginen valintasi.</p>`,
        a_en: `<p>The Vertailutyökalu helps gauge market level, but in a bidding contest consider:</p>
<ul>
<li><strong>How important is the estate to you?</strong> — If it fits your needs exceptionally, an aggressive bid may be justified.</li>
<li><strong>How fierce is the competition?</strong> — Attractive properties (shoreline, near town) draw more bids. Conservative may not suffice.</li>
<li><strong>Market trend:</strong> The Market Overview shows whether prices have risen or fallen in recent quarters.</li>
<li><strong>Asking vs estimate:</strong> If asking is significantly above the estimate, the estate is probably overpriced. If well below, the seller may be motivated to sell quickly.</li>
<li><strong>Time pressure:</strong> Is there a deadline for the deal (e.g. inheritance distribution)? Can you stretch negotiations or do you need a quick resolution?</li>
</ul>
<p>The tool's bid suggestions (low / median / high) provide starting points, but the final bid is always your strategic decision.</p>`,
      },
      {
        q_fi: 'Mistä saan apua, jos törmään ongelmaan?',
        q_en: 'Where can I get help if I run into a problem?',
        a_fi: `<p>Käytännön ohjeita ongelmatilanteisiin:</p>
<ul>
<li><strong>Salasana unohtunut:</strong> Ole yhteydessä palveluntarjoajaan — voi nollata salasanan.</li>
<li><strong>Sähköposti ei tunnistu jakoa otettaessa vastaan:</strong> Tarkista että rekisteröidyit täsmälleen samalla osoitteella johon jako kohdistettiin.</li>
<li><strong>Tila ei näy listalla:</strong> Tarkista suodattimet ("Tyhjennä"-nappi nollaa ne). Jos tila on uusi, se voi olla vielä lisäysjonossa.</li>
<li><strong>Kohdesivu näyttää virheellistä tietoa:</strong> Lähetä palaute palveluntarjoajalle. Anna tilan tunnistetiedot.</li>
<li><strong>Ehdotukset uusiin ominaisuuksiin:</strong> Aina tervetulleita.</li>
</ul>`,
        a_en: `<p>Practical help in common situations:</p>
<ul>
<li><strong>Forgotten password:</strong> Contact the service provider — they can reset it.</li>
<li><strong>Email not recognised when receiving a share:</strong> Check you registered with exactly the email the share was directed to.</li>
<li><strong>Estate doesn't show in the list:</strong> Check filters (the "Clear" button resets them). If new, it may still be in the ingestion queue.</li>
<li><strong>Estate page shows incorrect data:</strong> Send feedback to the service provider with the estate's identifying details.</li>
<li><strong>Feature suggestions:</strong> Always welcome.</li>
</ul>`,
      },
    ],
  },
];
